import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { TokenMapperInput } from '../../../api/types';
import {
  SynButtonComponent,
  SynergyFormsModule,
  SynIconComponent,
  SynInputComponent,
  SynOptionComponent,
  SynSelectComponent,
  SynValidateComponent,
} from '@synergy-design-system/angular';
import { FormsModule, NgForm } from '@angular/forms';
import { HelpTextComponent } from '../../components/help-text.component';
import { ActivatedRoute } from '@angular/router';
import { StateService } from '../../../api/state';

@Component({
  selector: 'app-token-mapper-form',
  template: `
    <form #form=ngForm class="flex-col gap-2 form-width" (submit)="onSubmit(form)">
      <syn-select name="type" label="Mapper Type" i18n-label="@@mapper-type" [disabled]="true"
                  [value]="mapperType()">
        <syn-option value="session" i18n="@@session">Session</syn-option>
        <syn-option value="userAttribute" i18n="@@user-attribute">User Attribute</syn-option>
        <syn-option value="audience" i18n="@@audience">Audience</syn-option>
        <syn-option value="realmRoles" i18n="@@realm-roles">Realm Roles</syn-option>
        <syn-option value="clientRoles" i18n="@@client-roles">Client Roles</syn-option>
      </syn-select>
      <app-help-text>
        <syn-validate on="invalid change">
          <syn-input
            label="Display Name" i18n-label="@@display-name"
            [(ngModel)]="this.mapper().displayName"
            [required]="true"
            [minlength]="3"
            name="displayName" />
        </syn-validate>
        <span helpText i18n="@@token-mapper-display-name-help">
          Human readable name for the token mapper.
          It has no influence on the token.
        </span>
      </app-help-text>

      <!-- For now we do not offer any kind of mapping target choices -->
      <!-- If this is really necessary, we can add it later or simply wait for SMAX tickets about the topic -->
      <!--      <div class="flex-row gap-1">-->
      <!--        <span>Add claim to:</span>-->
      <!--        <syn-checkbox i18n="@@add-to-id-token" [(ngModel)]="this.mapper().addToIdToken" name="addToIdToken">-->
      <!--          ID Token-->
      <!--        </syn-checkbox>-->
      <!--        <syn-checkbox i18n="@@add-to-access-token" [(ngModel)]="this.mapper().addToAccessToken" name="addToAccessToken">-->
      <!--          Access Token-->
      <!--        </syn-checkbox>-->
      <!--        <syn-checkbox i18n="@@add-to-user-info" [(ngModel)]="this.mapper().addToUserInfo" name="addToUserInfo">-->
      <!--          User Info-->
      <!--        </syn-checkbox>-->
      <!--      </div>-->

      @let session = mapper().session;
      @if (session) {
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="Claim Name" i18n-label="@@claim-name"
              [(ngModel)]="session.claimName"
              [required]="true"
              [minlength]="1"
              name="claimName" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-claim-name-help">
            The claim name identifies the field name in the token into which the value will be mapped.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="Session Note" i18n-label="@@session-note"
              [(ngModel)]="session.note"
              [required]="true"
              [minlength]="1"
              name="sessionNote" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-session-note-help">
            The part of the session information to map into the token.
          </span>
        </app-help-text>
      }
      @let userAttribute = mapper().userAttribute;
      @if (userAttribute) {
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="Claim Name" i18n-label="@@claim-name"
              [(ngModel)]="userAttribute.claimName"
              [required]="true"
              [minlength]="1"
              name="claimName" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-claim-name-help">
            The claim name identifies the field name in the token into which the value will be mapped.
          </span>
        </app-help-text>
        <!-- TODO: Should this be a select with predefined options? -->
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="User Attribute" i18n-label="@@user-attribute"
              [(ngModel)]="userAttribute.name"
              [required]="true"
              [minlength]="1"
              name="userAttributeName" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-user-attribute-help">
            Name of the user attribute that should be mapped into the token.
          </span>
        </app-help-text>
      }
      @let audience = mapper().audience;
      @if (audience) {
        <!-- TODO: We should offer a radio selection between these two mutually exclusive options -->
        <app-help-text>
          <syn-input
            label="Client Audience" i18n-label="@@client-audience"
            [(ngModel)]="audience.client"
            [required]="false"
            name="clientAudience" />
          <span helpText i18n="@@token-mapper-client-audience-help">
            Maps this client into the audience ('aud') field of the token.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-input
            label="Custom Audience" i18n-label="@@custom-audience"
            [(ngModel)]="audience.custom"
            [required]="false"
            name="customAudience" />
          <span helpText i18n="@@token-mapper-custom-audience-help">
            Maps this custom string into the audience ('aud') field of the token.
          </span>
        </app-help-text>
      }
      @let realmRoles = mapper().realmRoles;
      @if (realmRoles) {
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="Claim Name" i18n-label="@@claim-name"
              [(ngModel)]="realmRoles.claimName"
              [required]="true"
              [minlength]="1"
              name="claimName" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-claim-name-help">
            The claim name identifies the field name in the token into which the value will be mapped.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-input
            label="Role Prefix" i18n-label="@@role-prefix"
            [(ngModel)]="realmRoles.rolePrefix"
            [required]="false"
            name="rolePrefix" />
          <span helpText i18n="@@token-mapper-role-prefix-help">
            Appends a prefix to all mapped roles.
          </span>
        </app-help-text>
      }
      @let clientRoles = mapper().clientRoles;
      @if (clientRoles) {
        <app-help-text>
          <syn-validate on="invalid change">
            <syn-input
              label="Claim Name" i18n-label="@@claim-name"
              [(ngModel)]="clientRoles.claimName"
              [required]="true"
              [minlength]="1"
              name="claimName" />
          </syn-validate>
          <span helpText i18n="@@token-mapper-claim-name-help">
            The claim name identifies the field name in the token into which the value will be mapped.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-select
            label="Client" i18n-label="@@client"
            [(ngModel)]="mappedClientId"
            [required]="true"
            name="clientId">
            @for (client of clients(); track client.id) {
              <syn-option [value]="client.clientId">{{ client.clientId }}</syn-option>
            }
          </syn-select>
          <span helpText i18n="@@token-mapper-client-help">
            ID of the client for which all roles should be mapped.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-input
            label="Role Prefix" i18n-label="@@role-prefix"
            [(ngModel)]="clientRoles.rolePrefix"
            [required]="false"
            name="rolePrefix" />
          <span helpText i18n="@@token-mapper-role-prefix-help">
            Appends a prefix to all mapped roles.
          </span>
        </app-help-text>
      }

      <syn-button
        variant="filled"
        type="submit"
        [disabled]="!form.dirty || !!form.invalid"
      >
        <syn-icon [name]="submitIcon() + '_fill'" slot="prefix" />
        <span>{{ submitText() }}</span>
      </syn-button>
    </form>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynInputComponent,
    SynSelectComponent,
    SynOptionComponent,
    SynergyFormsModule,
    FormsModule,
    SynValidateComponent,
    HelpTextComponent,
  ],
})
export class TokenMapperFormComponent implements OnInit {
  clientId = inject(ActivatedRoute).snapshot.params['id'];

  mapper = input.required<TokenMapperInput>();
  submitIcon = input('');
  submitText = input('');
  create = input(false);
  submitMapper = output<TokenMapperInput>();
  mapperType = computed(() => {
    const mapper = this.mapper();
    if (mapper.session) return 'session';
    if (mapper.userAttribute) return 'userAttribute';
    if (mapper.audience) return 'audience';
    if (mapper.realmRoles) return 'realmRoles';
    if (mapper.clientRoles) return 'clientRoles';
    return '(unknown)';
  });
  mappedClientId = signal('');

  clients = computed(() => {
    return this.state.clients();
  });

  state = inject(StateService);

  ngOnInit(): void {
    this.state.queryClients(() => {
      const clientRoles = this.mapper().clientRoles;
      if (clientRoles) {
        this.mappedClientId.set(clientRoles.clientId);
      }
    });
  }


  onSubmit(form: NgForm) {
    if (form.invalid) return;

    const mapper: TokenMapperInput = this.mapper();
    if (mapper.audience) {
      if (mapper.audience.client && mapper.audience.client.length > 0) {
        delete mapper.audience.custom;
      } else {
        delete mapper.audience.client;
      }
    }
    if (mapper.clientRoles) {
      mapper.clientRoles.clientId = this.mappedClientId();
    }
    this.submitMapper.emit(mapper);
    form.form.markAsPristine();
  }
}
